import { darken, getContrastRatio, lighten } from "@material-ui/core/styles/colorManipulator";
import { TypeAction } from "@material-ui/core/styles/createPalette";
import { ElectricSettings } from "Types/api/ElectricSettings";

export interface PaletteColor {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
}

export interface Palette {
    action: TypeAction;
    colors: {
        blue: {
            20: string;
            60: string;
        };
    };
    common: {
        black: string;
        white: string;
    };
    cta: {
        background: string;
        text: string;
    };
    error: {
        main: string;
    };
    flyout: {
        background: string;
        text: string;
        icon: string;
    };
    grey: {
        10: string;
        13: string;
        15: string;
        20: string;
        40: string;
        60: string;
        70: string;
        80: string;
        100: string;
        120: string;
    };
    info: {
        main: string;
    };
    primary: PaletteColor;
    secondary: PaletteColor;
    success: {
        main: string;
    };
}

export interface Options {
    primary?: string;
    secondary?: string;
    cta_background?: string;
    cta_text?: string;
}

const COMMON_WHITE = "#ffffff";
const LIGHT_TEXT = "rgba(0, 0, 0, 0.87)";
const DARK_TEXT = COMMON_WHITE;
const TONAL_OFFSET = 0.2;
const CONTRAST_THRESHOLD = 3;

function getContrastText(background: string) {
    return getContrastRatio(background, DARK_TEXT) >= CONTRAST_THRESHOLD ? DARK_TEXT : LIGHT_TEXT;
}

const formatHexToColor = (color?: string): string | undefined => {
    // tslint:disable-next-line:strict-type-predicates
    if (typeof color !== "string") return undefined;
    if (color.startsWith("#")) return color;
    return "#" + color;
};

const createPalette = (settings?: ElectricSettings): Palette => {
    const primary = formatHexToColor(settings?.primary_color) || "#222222";
    const secondary = formatHexToColor(settings?.secondary_color) || "#2FAB36";

    return {
        action: {
            active: "rgba(0, 0, 0, 0.54)",
            disabled: "rgba(0, 0, 0, 0.26)",
            disabledBackground: "rgba(0, 0, 0, 0.12)",
            hover: "rgba(0, 0, 0, 0.08)",
            hoverOpacity: 0.08,
            selected: "rgba(0, 0, 0, 0.14)",
        },
        colors: {
            blue: {
                20: "#5baac8",
                60: "#0079c5",
            },
        },
        common: {
            black: "#3b3b3c",
            white: "#ffffff",
        },
        cta: {
            background: formatHexToColor(settings?.button_background_color) || secondary,
            text: formatHexToColor(settings?.button_text_color) || getContrastText(secondary),
        },
        error: {
            main: "#d0021b",
        },
        flyout: {
            background: formatHexToColor(settings?.tab_background) || secondary,
            text: formatHexToColor(settings?.tab_text_color) || getContrastText(secondary),
            icon: formatHexToColor(settings?.icon_fill) || getContrastText(secondary),
        },
        grey: {
            10: "#eaeaea",
            13: "#dddddd",
            15: "#cccccc",
            20: "#acacac",
            40: "#aaaaaa",
            60: "#8d8d8d",
            70: "#83898f",
            80: "#696969",
            100: "#434345",
            120: "#333333",
        },
        info: {
            main: "#477cbb",
        },
        primary: {
            contrastText: getContrastText(primary),
            dark: darken(primary, TONAL_OFFSET * 1.5),
            light: lighten(primary, TONAL_OFFSET),
            main: primary,
        },
        secondary: {
            contrastText: getContrastText(secondary),
            dark: darken(secondary, TONAL_OFFSET * 1.5),
            light: lighten(secondary, TONAL_OFFSET),
            main: secondary,
        },
        success: {
            main: "#6fc115",
        },
    };
};

export default createPalette;
